/**
 * MonitoringTab — customer-facing coverage transparency + setup journey for
 * the Baseline Compliance page (plan:
 * .agents/Tasks/High Priority/baseline-compliance-monitoring-ux-plan.md §5).
 *
 * Renders, in order, on Carbon (tiles in condensed grids, a DataTable, the
 * vertical progress indicator):
 *   1. Coverage — hero (locked monitoring-scope copy + provenance byline) and
 *      the three coverage channels (continuous watch / baseline scans /
 *      full-configuration capture), each honest about whether it is live.
 *   2. Setup journey — Connect → Verify → Size → Choose scope → Watch stepper,
 *      only while continuous monitoring is not yet active. Phase 2 makes the
 *      current step ACTIONABLE: script download/copy, paste-and-verify,
 *      sizing with a live poll, scope chips, and the switch-on.
 *   3. What we watch, by group — real per-group counts from the active watch
 *      list, plus the inventory footnote. Only once monitoring is live.
 *   4. How decisions work — Detect → Decide → Apply strip linking to the queue.
 *
 * ALL copy and conditional logic live in lib/monitoring-coverage.ts and
 * lib/monitoring-actions.ts (the vitest seams — this harness can't run
 * effects). This component is layout only: it maps the composed view models
 * onto the Carbon primitives. The actionable stepper (mutations +
 * step actions) is the shared SetupJourney component
 * (components/Onboarding/SetupJourney.tsx), which the first-run /setup
 * wizard renders too. No internal pipeline names (TCM/DSC/…) may appear in
 * anything it renders, and write errors ALWAYS route through
 * mapWriteErrorFrom — never raw ProblemDetails.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import clsx from 'clsx'

import { tcmApi } from '@/lib/api'
import {
  composeMonitoringCoverage,
  type CoverageChannel,
  type CoverageGroupRow,
} from '@/lib/monitoring-coverage'
import {
  coverageRefetchInterval,
  COVERAGE_POLL_MAX_FAILURES,
} from '@/lib/monitoring-actions'
import { tenantsCacheNeedsRefresh } from '@/lib/onboarding-setup'
import type { Tenant } from '@/types'
import type { TcmCoverageSummary } from '@/types/tcm'
import SetupJourney from '@/components/Onboarding/SetupJourney'
import { GroupSettings } from './MonitoringGroupSettings'
import { SectionHeadline } from '@/components/PostureBrief/SectionHeadline'
import { Button } from '@/components/carbon/Button'
import { CondensedGrid } from '@/components/carbon/CondensedGrid'
import { DataTable, TableItem, type DataColumn } from '@/components/carbon/DataTable'
import { IconArrowRight } from '@/components/carbon/icons'
import { InlineNotification } from '@/components/carbon/InlineNotification'
import { IuvoMark } from '@/components/carbon/IuvoMark'
import { Skeleton } from '@/components/carbon/Skeleton'
import { StatusDot } from '@/components/carbon/StatusDot'
import { Tile } from '@/components/carbon/Tile'
import { TileHeader } from '@/components/carbon/TileHeader'

export interface MonitoringTabProps {
  tenantId: string | undefined
}

export default function MonitoringTab({ tenantId }: MonitoringTabProps) {
  const [, setSearchParams] = useSearchParams()
  const queryClient = useQueryClient()

  const coverageQuery = useQuery({
    queryKey: ['tcm-coverage', tenantId],
    queryFn: async () => {
      const summary = await tcmApi.getCoverage(tenantId!)
      // Promotion flips Tenants.baselineSource server-side; the setup gate
      // (Layout banner + auto-route) reads the ['tenants'] cache (5-min
      // staleTime), so refresh it the moment this poll sees the transition
      // to Hybrid — else the "Setup isn't finished" reminder outlives the
      // finished setup (codex P2; same fix as SetupWizard). No-op for
      // settled fleet tenants whose cache already reads Hybrid.
      if (
        tenantsCacheNeedsRefresh(
          summary.baselineSource,
          queryClient.getQueryData<Tenant[]>(['tenants']),
          tenantId!,
        )
      ) {
        void queryClient.invalidateQueries({ queryKey: ['tenants'] })
      }
      return summary
    },
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    // Bounded poll for the self-serve auto-finish window (W9): while the server
    // is auto-driving size → scope → activate, or the monitor is live but the
    // deferred born-Hybrid promote hasn't flipped to Hybrid yet, this page
    // advances on its own. The cadence decision is a pure function so it stays
    // unit-testable — it polls only through {Connected, Sized, Finishing} and
    // stops on Active/Held/manual-wait or after 3 consecutive failures.
    refetchInterval: query =>
      coverageRefetchInterval(
        query.state.data as TcmCoverageSummary | undefined,
        query.state.fetchFailureCount,
      ),
  })

  const view = composeMonitoringCoverage({
    coverage: coverageQuery.data,
    isLoading: coverageQuery.isLoading,
    isError: coverageQuery.isError,
    hasTenant: !!tenantId,
  })

  // ── Setup journey (extracted to components/Onboarding/SetupJourney) ─
  // All journey state, mutations, and step actions live in SetupJourney so
  // the first-run /setup wizard (pages/SetupWizard.tsx) renders the same
  // machinery. This tab only threads coverage + the stalled-poll signal
  // through; the key remount resets journey-local state on a tenant switch.
  const coverage = coverageQuery.data
  const pollStalled = coverageQuery.failureCount >= COVERAGE_POLL_MAX_FAILURES

  // Tab hop without a full page load — PillarTabs keys the active tab off
  // ?tab=…, so we just rewrite that one param and let the existing URL sync
  // do the rest (tenant param survives untouched).
  const openQueue = () => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      next.set('tab', 'drifts')
      return next
    })
  }

  if (view.state === 'no-tenant') {
    return (
      <QuietPanel>
        Pick a tenant from the side navigation to see what Guardian watches for it.
      </QuietPanel>
    )
  }

  if (view.state === 'loading') {
    return <Skeleton lines={4} label="Reading your coverage" className="mt-2 max-w-2xl" />
  }

  if (view.state === 'error') {
    return (
      <InlineNotification
        kind="error"
        title="We could not load your coverage view."
        actions={
          <Button kind="tertiary" size="sm" onClick={() => void coverageQuery.refetch()}>
            Retry
          </Button>
        }
      >
        Nothing is wrong with your monitoring itself. Try again in a moment.
      </InlineNotification>
    )
  }

  return (
    <div className="flex flex-col gap-12">
      {/* ── Coverage hero + channels ─────────────────────────────────── */}
      <section aria-label="Coverage">
        <SectionHeadline level="page">
          {view.hero.headline}
          {view.hero.headlineEm}
        </SectionHeadline>
        <div className="mt-2 flex max-w-[56ch] flex-col gap-1.5">
          {view.hero.deck.map((sentence, i) => (
            <p
              key={i}
              className={clsx('m-0', i === 0 ? 'text-body text-text-secondary' : 'text-helper text-text-helper')}
            >
              {sentence}
            </p>
          ))}
        </div>
        <p className="m-0 mt-4 flex items-center gap-2 text-helper text-text-helper">
          <IuvoMark size={14} />
          <span>Assessed by Guardian · {view.hero.bylineMeta}</span>
        </p>

        <CondensedGrid as="ul" columns={{ base: 1, 'c-md': 3 }} className="mt-6">
          {view.channels.map(ch => (
            <ChannelTile key={ch.id} channel={ch} />
          ))}
        </CondensedGrid>
      </section>

      {/* ── Setup journey (only until monitoring is live) ────────────── */}
      {view.journey && (
        <section aria-label="Setup journey">
          <SectionHeadline level="page">{view.journey.headline}</SectionHeadline>
          <p className="m-0 mt-2 max-w-[56ch] text-body text-text-secondary">{view.journey.sub}</p>
          {view.journey.caveat && (
            <InlineNotification kind="info" title={view.journey.caveat} className="mt-4 max-w-2xl" />
          )}
          {coverage && tenantId && (
            <SetupJourney
              key={tenantId}
              tenantId={tenantId}
              coverage={coverage}
              journey={view.journey}
              pollStalled={pollStalled}
              onCheckAgain={() => void coverageQuery.refetch()}
            />
          )}
        </section>
      )}

      {/* ── What we watch, by group ──────────────────────────────────── */}
      {view.groups && (
        <section aria-label="What we watch">
          {view.groupsCaveat && (
            <InlineNotification kind="info" title={view.groupsCaveat} className="mb-4 max-w-2xl" />
          )}
          <DataTable
            title="What we watch"
            description="Coverage is organised around outcomes, not products. Expand a group to see each setting Guardian watches."
            columns={GROUP_COLUMNS}
            rows={view.groups}
            rowId={g => g.group}
            expandable={{
              render: row => <GroupSettings settings={row.settings ?? []} />,
              isExpandable: row => !!row.settings?.length,
            }}
            size="md"
            footer={<span className="text-helper text-text-helper">{view.inventoryNote}</span>}
          />
        </section>
      )}

      {/* When groups are absent (pre-activation), the inventory stance still
          deserves a home — it answers "why don't I see my users here?". */}
      {!view.groups && <p className="m-0 max-w-[56ch] text-helper text-text-helper">{view.inventoryNote}</p>}

      {/* ── How decisions work ───────────────────────────────────────── */}
      <section aria-label="How decisions work">
        <SectionHeadline level="page">What happens when a watched setting changes.</SectionHeadline>
        <p className="m-0 mt-2 max-w-[56ch] text-body text-text-secondary">
          Guardian finds the change and ranks it. Accepting and applying are yours.
        </p>
        <CondensedGrid as="ol" columns={{ base: 1, 'c-md': 3 }} className="mt-6">
          {view.lifecycle.map(step => (
            <Tile key={step.numeral} as="li" className="flex flex-col gap-2">
              <TileHeader
                title={step.title}
                level={3}
                lead={<span className="tabular-nums text-text-helper">{step.numeral}</span>}
              />
              <p className="m-0 text-text-secondary">{step.body}</p>
            </Tile>
          ))}
        </CondensedGrid>
        <Button kind="ghost" size="md" icon={<IconArrowRight />} onClick={openQueue} className="-ml-4 mt-4">
          Open the queue
        </Button>
      </section>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Pieces                                                                     */
/* -------------------------------------------------------------------------- */

function QuietPanel({ children }: { children: React.ReactNode }) {
  return (
    <Tile className="py-10 text-center text-text-helper">
      {children}
    </Tile>
  )
}

/**
 * One coverage channel as a Carbon tile: the title, a status word (a strong
 * dot for live, a neutral one otherwise — never a hue), the six-word verdict,
 * the body, and its meta line, caveat or journey pointer.
 */
function ChannelTile({ channel }: { channel: CoverageChannel }) {
  return (
    <Tile as="li" className="flex min-w-0 flex-col gap-3">
      <TileHeader
        title={channel.title}
        level={3}
        aside={
          <span className="inline-flex items-center gap-2 text-helper text-text-secondary">
            <StatusDot tone={channel.active ? 'active' : 'neutral'} label={channel.active ? 'Live' : 'Not yet live'} />
            <span aria-hidden="true">{channel.active ? 'Live' : 'Not yet live'}</span>
          </span>
        }
      />
      <p className="m-0 text-text-primary">{channel.verdict}</p>
      <p className="m-0 text-text-secondary">{channel.body}</p>
      {channel.meta && (
        <p className="m-0 border-t border-line-subtle pt-3 text-helper tabular-nums text-text-primary">{channel.meta}</p>
      )}
      {channel.caveat && <p className="m-0 text-helper text-text-helper">{channel.caveat}</p>}
      {channel.inactiveNote && <p className="m-0 text-helper text-text-helper">{channel.inactiveNote}</p>}
    </Tile>
  )
}

/** The watched groups: what each protects, and whether it is in scope with its real count. */
const GROUP_COLUMNS: DataColumn<CoverageGroupRow>[] = [
  {
    id: 'group',
    header: 'Group',
    primary: true,
    cell: row => <TableItem title={row.title} sub={row.description} />,
  },
  {
    id: 'watched',
    header: 'Coverage',
    nowrap: true,
    align: 'end',
    cell: row =>
      row.watched === true ? (
        <span className="inline-flex items-center gap-2">
          <StatusDot tone="active" label="Watched" />
          <span className="tabular-nums text-text-primary">{row.countLabel ?? 'Watched'}</span>
        </span>
      ) : row.watched === false ? (
        <span className="text-text-helper">Not in scope</span>
      ) : (
        <span className="text-text-helper">Unknown</span>
      ),
  },
]
